import type { PointerEvent as ReactPointerEvent } from 'react';
import { useRef, useState } from 'react';
import { t } from '../i18n';

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

interface ScrubberProps {
  position: number;
  duration: number;
  bufferedEnd: number;
  onSeek: (seconds: number) => void;
}

/** Seek bar for movies and episodes: click or drag; times on both sides. */
export function Scrubber({ position, duration, bufferedEnd, onSeek }: ScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<number | null>(null);
  const valid = Number.isFinite(duration) && duration > 0;

  const timeAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || !valid || rect.width === 0) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) * duration;
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!valid) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setPreview(timeAt(event.clientX));
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (preview !== null) setPreview(timeAt(event.clientX));
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (preview === null) return;
    onSeek(timeAt(event.clientX));
    setPreview(null);
  };

  const shown = preview ?? position;
  const percent = valid ? (shown / duration) * 100 : 0;
  const bufferedPercent = valid ? Math.min(100, (bufferedEnd / duration) * 100) : 0;
  return (
    <div className="scrubber">
      <span className="scrubber__time">{formatTime(shown)}</span>
      <div
        ref={trackRef}
        className="scrubber__track"
        role="slider"
        aria-label={t('player.seek')}
        aria-valuemin={0}
        aria-valuemax={valid ? Math.round(duration) : 0}
        aria-valuenow={Math.round(shown)}
        aria-valuetext={`${formatTime(shown)} of ${formatTime(duration)}`}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setPreview(null)}
      >
        <div className="scrubber__buffered" style={{ width: `${bufferedPercent}%` }} />
        <div className="scrubber__played" style={{ width: `${percent}%` }} />
        <div className="scrubber__thumb" style={{ left: `${percent}%` }} />
      </div>
      <span className="scrubber__time">{valid ? formatTime(duration) : '--:--'}</span>
    </div>
  );
}
